import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Barcode as BarcodeIcon, Plus, FileText, AlertCircle, Loader, Activity, TrendingUp } from 'lucide-react';
import { recordService } from '../../services/api';

const VehicleExecutiveDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    recentRecords: [],
    stats: {
      thisMonth: 0,
      pending: 0,
      completed: 0,
    },
    loading: true,
    error: null,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsResponse, statsResponse] = await Promise.all([
        recordService.getAll({ limit: 20 }),
        recordService.getStatistics(),
      ]);

      // API returns: { success, message, data: { records: [...], limit, page } }
      const recordsData = recordsResponse.data?.data?.records || [];
      const statsData = statsResponse.data?.data || {};

      setData({
        recentRecords: recordsData,
        stats: {
          thisMonth: recordsData.length,
          pending: statsData.pending || 0,
          completed: statsData.completed || 0,
        },
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Dashboard error:', err);
      setData((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load records',
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-300';
      case 'ASSISTED':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-300';
      case 'HOLD':
        return 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-300';
      case 'RETURN':
        return 'bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border border-red-300';
      default:
        return 'bg-slate-50 text-slate-700 border border-slate-200';
    }
  };

  const getStatusBadgeGradient = (status) => {
    switch (status) {
      case 'PENDING':
        return 'from-blue-500 to-indigo-600';
      case 'ASSISTED':
        return 'from-green-500 to-emerald-600';
      case 'HOLD':
        return 'from-amber-500 to-orange-600';
      case 'RETURN':
        return 'from-red-500 to-rose-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  if (data.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full mb-4">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <p className="text-slate-700 font-semibold">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="mb-2">
        <h2 className="text-5xl font-bold text-slate-950 tracking-tight">Vehicle Executive Dashboard</h2>
        <p className="text-slate-500 mt-3 text-base font-medium">Create and manage service records for vehicles</p>
      </div>

      {/* Error Message */}
      {data.error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-xl flex items-center gap-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm font-medium text-red-900">{data.error}</p>
        </div>
      )}

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button 
          onClick={() => navigate('/scan')}
          className="group bg-blue-600 rounded-2xl shadow-sm p-8 text-white hover:shadow-md hover:bg-blue-700 transition border border-blue-500"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="text-left">
              <h3 className="text-2xl font-bold mb-2">Scan Barcode</h3>
              <p className="text-blue-100 font-medium">Open camera to scan vehicle</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-xl group-hover:bg-opacity-30 transition">
              <BarcodeIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="w-full bg-white hover:bg-blue-50 text-blue-600 font-bold py-3 rounded-xl transition">
            Open Scanner
          </div>
        </button>

        <button 
          onClick={() => navigate('/create-record')}
          className="group bg-emerald-600 rounded-2xl shadow-sm p-8 text-white hover:shadow-md hover:bg-emerald-700 transition border border-emerald-500"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="text-left">
              <h3 className="text-2xl font-bold mb-2">Create Record</h3>
              <p className="text-green-100 font-medium">Add a new service record</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-xl group-hover:bg-opacity-30 transition">
              <Plus className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="w-full bg-white hover:bg-green-50 text-green-600 font-bold py-3 rounded-xl transition">
            New Record
          </div>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Records This Month', value: data.stats.thisMonth, icon: Activity, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50' },
          { label: 'Pending Records', value: data.stats.pending, icon: FileText, color: 'from-amber-500 to-orange-600', bgColor: 'bg-amber-50' },
          { label: 'Completed Records', value: data.stats.completed, icon: TrendingUp, color: 'from-green-500 to-emerald-600', bgColor: 'bg-green-50' },
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div
              key={idx}
              className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200 p-8 hover:shadow-xl transition"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-600 text-sm font-semibold uppercase tracking-wide">{stat.label}</p>
                  <p className="text-4xl font-bold text-slate-900 mt-3">{stat.value}</p>
                </div>
                <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Records */}
      <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900">Recent Records</h3>
            <p className="text-slate-600 text-sm mt-1">Latest service records you've created</p>
          </div>
        </div>

        {data.recentRecords.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border-2 border-dashed border-slate-300">
            <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-700 font-bold text-lg">No records yet</p>
            <p className="text-slate-600">Start by creating your first service record</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.recentRecords.map((record) => (
              <div
                key={record.id}
                className={`flex items-center justify-between p-6 rounded-xl border-2 transition hover:shadow-md ${getStatusColor(record.status)}`}
              >
                <div className="flex items-center gap-5 flex-1">
                  <div className={`p-3 bg-gradient-to-br ${getStatusBadgeGradient(record.status)} rounded-xl shadow-lg`}>
                    <BarcodeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-lg">{record.barcodeNumber}</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Created {record.createdAt ? new Date(record.createdAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-4 py-2 rounded-lg text-sm font-bold ${getStatusColor(record.status)}`}>
                    {record.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border-2 border-indigo-300 p-8 shadow-lg">
        <div className="flex gap-6">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h4 className="text-lg font-bold text-indigo-900 mb-4">Getting Started Guide</h4>
            <ol className="space-y-3 text-slate-700 font-medium">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <span>Click "Scan Barcode" to open the camera and scan a vehicle barcode</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <span>Review the vehicle details that appear after scanning</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <span>Add service details and submit the record</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                <span>Track record status here as service engineers process them</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleExecutiveDashboard;
